import { useState, useMemo, useEffect } from "react";
import dayjs from "dayjs";
import { toast, Toaster } from "sonner";
import { DashboardLayout } from "components/layout/components/DashboardLayout";
import { Navbar } from "components/layout/components/Navbar";
import { useHandleInvite } from "features/invites/hooks/useHandleInvite";
import { useFetchTalentInvites } from "../hooks/useFetchTalentInvites";
import { ScaleLoader } from "react-spinners";
import { useTour } from "components/layout/components/tours/TourGuideContext";
import { generateDemoInvites } from "features/demo/data";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { useNavigate, useParams } from "react-router-dom";
import { routePath } from "config/routeMgt/routePath";
import { useGetPendingFeedback } from "features/feedback/hooks/useGetPendingFeedback";
import { MapPin } from "lucide-react";

const INVITE_TAB_IDS = ["pending", "accepted", "declined", "offers"] as const;
type InviteTabId = (typeof INVITE_TAB_IDS)[number];

export const Invite = () => {
  const { running, activeTour, stepIndex } = useTour();
  const navigate = useNavigate();
  const { status: statusParam } = useParams<{ status?: string }>();
  const { data, isLoading } = useFetchTalentInvites();
  const { userInfo } = useGetUserInfo();
  const demodata = generateDemoInvites();
  const [activeTab, setActiveTab] = useState<InviteTabId>("pending");

  const { data: pendingFeedbackData, isLoading: isFeedbackLoading } =
    useGetPendingFeedback();

  useEffect(() => {
    if (!statusParam) return;
    if (INVITE_TAB_IDS.includes(statusParam as InviteTabId)) {
      setActiveTab(statusParam as InviteTabId);
    } else {
      navigate(routePath.invites, { replace: true });
    }
  }, [statusParam, navigate]);

  const [searchQuery, setSearchQuery] = useState("");

  const showTourSamples =
    running && activeTour === "talent" && stepIndex >= 4;
  const invitesData = showTourSamples ? demodata : data;

  const showLoader = !showTourSamples && isLoading;

  const pendingInvites = invitesData
    ? invitesData.filter((invite) => invite.status === 0)
    : [];
  const progressedInvites = invitesData
    ? invitesData.filter((invite) => invite.status === 1)
    : [];
  const declinedInvites = invitesData
    ? invitesData.filter((invite) => invite.status === 2)
    : [];

  const jobOffers = useMemo(() => {
    if (!pendingFeedbackData) return [];

    const uniqueMap = new Map();
    pendingFeedbackData.forEach((job: any) => {
      if (!uniqueMap.has(job.id)) {
        uniqueMap.set(job.id, job);
      }
    });

    return Array.from(uniqueMap.values());
  }, [pendingFeedbackData]);

  const filterBySearch = (items: any[]) => {
    if (!searchQuery.trim()) return items;

    const query = searchQuery.toLowerCase();
    return items.filter((item) => {
      if (item.listing) {
        return (
          item.listing.title?.toLowerCase().includes(query) ||
          item.listing.company_name?.toLowerCase().includes(query) ||
          item.listing.location_city?.toLowerCase().includes(query) ||
          item.listing.type?.toLowerCase().includes(query) ||
          item.listing.description?.toLowerCase().includes(query)
        );
      }
      return (
        item.title?.toLowerCase().includes(query) ||
        item.company_name?.toLowerCase().includes(query) ||
        item.location_city?.toLowerCase().includes(query) ||
        item.type?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query)
      );
    });
  };

  const filteredPendingInvites = useMemo(
    () => filterBySearch(pendingInvites),
    [pendingInvites, searchQuery],
  );

  const filteredProgressedInvites = useMemo(
    () => filterBySearch(progressedInvites),
    [progressedInvites, searchQuery],
  );

  const filteredDeclinedInvites = useMemo(
    () => filterBySearch(declinedInvites),
    [declinedInvites, searchQuery],
  );

  const filteredJobOffers = useMemo(
    () => filterBySearch(jobOffers),
    [jobOffers, searchQuery],
  );

  const getActiveTabData = () => {
    switch (activeTab) {
      case "pending":
        return filteredPendingInvites;
      case "accepted":
        return filteredProgressedInvites;
      case "declined":
        return filteredDeclinedInvites;
      case "offers":
        return filteredJobOffers;
      default:
        return [];
    }
  };

  const activeTabData = getActiveTabData();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  const formatDate = (date?: string) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <DashboardLayout>
      <Toaster position="top-right" richColors closeButton />
      <Navbar
        description={userInfo?.current_team?.name || "View available Invites"}
        title="Invites"
      />

      <div className="flex gap-6 mt-6">
        <div className="w-56 bg-white border border-gray-200 rounded-xl p-4 space-y-2">
          {[
            {
              id: "pending",
              label: "Pending",
              count: filteredPendingInvites.length,
            },
            {
              id: "accepted",
              label: "Accepted",
              count: filteredProgressedInvites.length,
            },
            {
              id: "declined",
              label: "Declined",
              count: filteredDeclinedInvites.length,
            },
            {
              id: "offers",
              label: "Job Offers",
              count: filteredJobOffers.length,
            },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                const id = tab.id as InviteTabId;
                setActiveTab(id);
                navigate(`${routePath.invites}/${id}`);
              }}
              disabled={
                showLoader || (tab.id === "offers" && isFeedbackLoading)
              }
              className={`w-full text-left py-2 px-3 rounded-lg flex items-center justify-between ${
                activeTab === tab.id
                  ? "bg-blue-50 text-blue-600 font-medium"
                  : "text-gray-600 hover:bg-gray-50"
              } ${showLoader || (tab.id === "offers" && isFeedbackLoading) ? "opacity-70 cursor-not-allowed" : ""}`}
            >
              <span>{tab.label}</span>
              {(showLoader && activeTab === tab.id) ||
              (tab.id === "offers" && isFeedbackLoading) ? (
                <ScaleLoader
                  color="var(--app-color-primary)"
                  height={15}
                  width={2}
                  margin={1}
                />
              ) : tab.count > 0 ? (
                <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full">
                  {tab.count}
                </span>
              ) : null}
            </button>
          ))}
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <div className="flex justify-between items-center bg-white border border-gray-200 rounded-xl px-4 py-3 mb-6 shadow-sm">
            <div className="relative w-96">
              <i className="ri-search-line absolute left-3 top-3 text-gray-500 text-lg"></i>
              <input
                type="text"
                placeholder="Search Invites"
                value={searchQuery}
                onChange={handleSearchChange}
                disabled={
                  showLoader || (activeTab === "offers" && isFeedbackLoading)
                }
                className={`w-full border border-gray-200 rounded-lg pl-10 pr-10 py-2 focus:outline-none ${
                  showLoader || (activeTab === "offers" && isFeedbackLoading)
                    ? "bg-gray-50 cursor-not-allowed"
                    : ""
                }`}
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                  type="button"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">
                {activeTabData.length}{" "}
                {activeTab === "offers" ? "offer" : "invite"}
                {activeTabData.length !== 1 ? "s" : ""}
                {searchQuery && ` matching "${searchQuery}"`}
              </span>
              
            </div>
          </div>

          {/* Invites List */}
          <div className="mt-8 max-h-[calc(100vh-260px)] overflow-y-auto pr-2">
            {showLoader || (activeTab === "offers" && isFeedbackLoading) ? (
              <div className="flex justify-center items-center py-20">
                <ScaleLoader
                  color="var(--app-color-primary)"
                  height={30}
                  width={3}
                  margin={2}
                />
              </div>
            ) : activeTabData.length > 0 ? (
              <div className="grid grid-cols-1 gap-6">
                {activeTab === "offers"
                  ? 
                    filteredJobOffers.map((job) => (
                      <JobOfferCard
                        key={job.id}
                        job={job}
                        onViewDetails={() =>
                          navigate(`${routePath.confirmJobOffer}/${job.id}`)
                        }
                      />
                    ))
                  : 
                    activeTabData.map((invite) => (
                      <InviteCard
                        key={invite.id}
                        invite={invite}
                        tab={activeTab}
                      />
                    ))}
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
                <div className="flex flex-col items-center justify-center">
                  <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-mail-line text-3xl text-gray-400"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">
                    {searchQuery
                      ? `No ${activeTab} ${activeTab === "offers" ? "offers" : "invites"} found for "${searchQuery}"`
                      : `No ${activeTab} ${activeTab === "offers" ? "offers" : "invites"}`}
                  </h3>
                  <p className="text-gray-500">
                    {searchQuery
                      ? "Try searching with different keywords"
                      : activeTab === "pending"
                        ? "You don't have any pending invites"
                        : activeTab === "accepted"
                          ? "You haven't accepted any invites yet"
                          : activeTab === "declined"
                            ? "You haven't declined any invites"
                            : "You don't have any job offers"}
                  </p>
                  {searchQuery && (
                    <button
                      onClick={clearSearch}
                      className="mt-4 px-4 py-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Clear search
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

const DeclineModal = ({
  isOpen,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) => {
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [customReason, setCustomReason] = useState("");
  const isOtherSelected = selectedReason === "Other";

  const handleConfirmClick = () => {
    if (!selectedReason || (isOtherSelected && !customReason.trim())) {
      toast.error("Please select a reason or enter a custom reason");
      return;
    }

    const finalReason = isOtherSelected ? customReason : selectedReason;
    onConfirm(finalReason);
    onClose();
  };

  if (!isOpen) return null;

  const declineReasons = [
    "Accepted another job offer",
    "Location or work mode is not suitable",
    "Long or unclear recruitment process",
    "Compensation not suitable",
    "Role not a good fit",
    "Limited growth opportunity",
    "Other",
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Reason for Decline
        </h3>

        <div className="space-y-3 mb-6">
          {declineReasons.map((reason) => (
            <div key={reason} className="flex items-center">
              <input
                type="radio"
                id={`reason-${reason}`}
                name="decline-reason"
                checked={selectedReason === reason}
                onChange={() => setSelectedReason(reason)}
                className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <label
                htmlFor={`reason-${reason}`}
                className="ml-3 text-sm text-gray-700 cursor-pointer"
              >
                {reason}
              </label>
            </div>
          ))}
        </div>

        {isOtherSelected && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Please specify
            </label>
            <textarea
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              rows={3}
              placeholder="Enter your reason for declining..."
              autoFocus
            />
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirmClick}
            disabled={
              !selectedReason || (isOtherSelected && !customReason.trim())
            }
            className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Confirm Decline
          </button>
        </div>
      </div>
    </div>
  );
};

const InviteCard = ({ invite, tab }: { invite: any; tab: string }) => {
  const { InviteSubmit, declineInvite, isLoading, isDeclining } =
    useHandleInvite();
  const [selectedDateId, setSelectedDateId] = useState<number | null>(
    invite.dates?.[0]?.id || null,
  );
  const [showDeclineModal, setShowDeclineModal] = useState(false);

  const handleDateSelect = (dateId: number) => {
    setSelectedDateId(dateId);
  };

  const handleAccept = () => {
    if (!selectedDateId && invite.dates?.length > 0) {
      setSelectedDateId(invite.dates[0].id);
    }

    InviteSubmit({
      status: 1,
      listing_invite_id: invite.id,
      invite_date_id: selectedDateId || invite.dates?.[0]?.id,
    });
  };

  const handleDeclineClick = () => {
    setShowDeclineModal(true);
  };

  const handleDeclineConfirm = (reason: string) => {
    declineInvite(
      {
        listing_invite_id: invite.id,
      },
      reason,
    );
    setShowDeclineModal(false);
  };

  const getStatusBadge = () => {
    switch (tab) {
      case "pending":
        return (
          <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
            Pending
          </span>
        );
      case "accepted":
        return (
          <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
            Accepted
          </span>
        );
      case "declined":
        return (
          <span className="px-3 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
            Declined
          </span>
        );
      case "offers":
        return (
          <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
            Job Offer
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                <i className="ri-briefcase-line text-xl text-blue-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {invite.listing.title}
                </h3>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-sm text-gray-600 flex items-center">
                    <i className="ri-building-line mr-1"></i>
                    {invite.listing.company_name}
                  </span>
                  <span className="text-sm text-gray-600 flex items-center">
                    <i className="ri-map-pin-line mr-1"></i>
                    {invite.listing.location_city}
                  </span>
                  <span className="text-sm text-gray-600 flex items-center">
                    <i className="ri-briefcase-line mr-1"></i>
                    {invite.listing.type}
                  </span>
                  {getStatusBadge()}
                </div>
              </div>
            </div>

            <div className="ml-15 pl-1">
              <p className="text-sm text-gray-600 mb-3">
                {invite.listing.description
                  ?.replace(/<[^>]*>/g, "")
                  .substring(0, 150)}
                ...
              </p>

              {invite.dates && invite.dates.length > 0 && tab === "pending" && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Interview Date:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {invite.dates.map((dateItem: any) => (
                      <button
                        key={dateItem.id}
                        type="button"
                        onClick={() => handleDateSelect(dateItem.id)}
                        className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                          selectedDateId === dateItem.id
                            ? "bg-blue-600 border-blue-600 text-white"
                            : "bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        {dayjs(dateItem.date).format("DD/MM/YYYY")}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col items-end gap-4 min-w-[180px]">
            <div className="text-right">
              <p className="text-xs text-gray-500">Invite Sent</p>
              <p className="text-sm font-medium text-gray-900">
                {dayjs(invite.created_at).format("DD/MMM/YYYY")}
              </p>

              {tab === "accepted" && (
                <>
                  <p className="text-xs text-gray-500 mt-2">Invite Accepted</p>
                  <p className="text-sm font-medium text-gray-900">
                    {dayjs(invite.updated_at).format("DD/MMM/YYYY")}
                  </p>
                </>
              )}
            </div>

            {tab === "pending" && (
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleDeclineClick}
                  disabled={isDeclining}
                  className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  {isDeclining ? "Declining..." : "Decline"}
                </button>
                <button
                  type="button"
                  onClick={handleAccept}
                  disabled={
                    isLoading || (invite.dates?.length > 0 && !selectedDateId)
                  }
                  className="px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {isLoading ? "Processing..." : "Accept"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <DeclineModal
        isOpen={showDeclineModal}
        onClose={() => setShowDeclineModal(false)}
        onConfirm={handleDeclineConfirm}
      />
    </>
  );
};

const JobOfferCard = ({
  job,
  onViewDetails,
}: {
  job: any;
  onViewDetails: () => void;
}) => {
  const formatDate = (date?: string) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="rounded-2xl border border-[#EAECF0] bg-white p-5 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-1 gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#4F46E5] to-[#7C3AED] text-lg font-bold text-white shadow-md">
            {job.company_name
              ?.split(" ")
              .map((word: string) => word[0])
              .join("")
              .slice(0, 2)
              .toUpperCase() || "CS"}
          </div>

          <div className="flex-1">
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <h2 className="text-xl font-bold leading-tight text-[#101828]">
                {job.title}
              </h2>
              <span className="rounded-full bg-[#EEF2FF] px-3 py-1 text-xs font-semibold text-[#4338CA]">
                Job Offer
              </span>
            </div>

            <div className="mb-4 flex flex-wrap items-center gap-3 text-sm text-[#667085]">
              <span className="font-semibold text-[#344054]">
                {job.company_name}
              </span>
              <span className="text-[#D0D5DD]">•</span>
              <div className="flex items-center gap-1">
                <MapPin size={14} className="text-[#667085]" />
                <span>
                  {job.location_city}, {job.location_country}
                </span>
              </div>
              <span className="text-[#D0D5DD]">•</span>
              <span className="capitalize font-medium">{job.type}</span>
            </div>

            <p className="max-w-2xl text-sm leading-relaxed text-[#475467]">
              <span className="font-semibold text-[#344054]">
                {job.company_name}
              </span>{" "}
              has indicated that you received an offer for the
              <span className="font-semibold text-[#344054]">
                {" "}
                {job.title}
              </span>{" "}
              position. Review the details carefully and confirm the status of
              this offer.
            </p>
          </div>
        </div>

        <div className="flex flex-col items-start gap-3 lg:items-end lg:justify-center">
          <div className="min-w-[140px] rounded-xl border border-[#EAECF0] bg-[#F9FAFB] px-4 py-3 text-left lg:text-right">
            <p className="text-xs font-semibold uppercase tracking-wide text-[#98A2B3]">
              Date Sent
            </p>
            <p className="mt-1 text-sm font-bold text-[#101828]">
              {formatDate(job.created_at)}
            </p>
          </div>

          <button
            onClick={onViewDetails}
            className="w-full rounded-full bg-[#000B7A] px-6 py-3 text-sm font-semibold text-white shadow-md transition-all hover:-translate-y-0.5 hover:bg-[#00095F] hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#000B7A]/20 lg:w-auto"
          >
            View Details
          </button>
        </div>
      </div>
    </div>
  );
};
